import { queryOptions, useQuery } from '@tanstack/react-query';

import { makeIsEnabled } from '@cast/utils';

import { apiClient } from '@cast/infrastructure-api';

import { useOrganizations } from '@cast/shared-organization-context-data-access';

type CloudVolumeMetricsStep = '1m' | '5m' | '1h';

const CLOUD_VOLUME_METRICS_QUERY_KEY = 'cloud-volume-metrics';
const getCloudVolumeMetricsQueryKey = (params: {
  organizationId: string | undefined;
  clusterId: string | undefined;
  nodeName: string | undefined;
  volumeId: string | undefined;
  startTime: string | undefined;
  endTime: string | undefined;
  step: CloudVolumeMetricsStep;
}) =>
  [
    CLOUD_VOLUME_METRICS_QUERY_KEY,
    params.organizationId,
    params.clusterId,
    params.nodeName,
    params.volumeId,
    params.startTime,
    params.endTime,
    params.step,
  ] as const;

type CloudVolumeMetricsQueryParams = {
  organizationId: string | undefined;
  clusterId: string | undefined;
  nodeName: string | undefined;
  volumeId: string | undefined;
  startTime: string | undefined;
  endTime: string | undefined;
  step: CloudVolumeMetricsStep;
  enabled?: boolean;
};

const getCloudVolumeMetricsQueryOptions = ({
  organizationId,
  clusterId,
  nodeName,
  volumeId,
  startTime,
  endTime,
  step,
  enabled,
}: CloudVolumeMetricsQueryParams) =>
  queryOptions({
    queryKey: getCloudVolumeMetricsQueryKey({
      organizationId,
      clusterId,
      nodeName,
      volumeId,
      startTime,
      endTime,
      step,
    }),
    queryFn: async () => {
      const { data } = await apiClient.stop.getCloudVolumeMetrics({
        organizationId: organizationId!,
        clusterId: clusterId!,
        nodeName: nodeName!,
        volumeId: volumeId!,
        startTime,
        endTime,
        step,
      });

      return data;
    },
    enabled: makeIsEnabled(
      !!organizationId,
      !!clusterId,
      !!nodeName,
      !!volumeId,
      enabled
    ),
  });

type UseCloudVolumeMetricsQueryArgs = {
  clusterId?: string;
  nodeName?: string;
  volumeId?: string;
  startTime?: string;
  endTime?: string;
  step?: CloudVolumeMetricsStep;
  enabled?: boolean;
};

export const useCloudVolumeMetricsQuery = ({
  clusterId,
  nodeName,
  volumeId,
  startTime,
  endTime,
  step = '1h',
  enabled,
}: UseCloudVolumeMetricsQueryArgs) => {
  const { currentOrganization } = useOrganizations();

  const { data, isLoading, isFetching, error, refetch } = useQuery(
    getCloudVolumeMetricsQueryOptions({
      organizationId: currentOrganization?.id,
      clusterId,
      nodeName,
      volumeId,
      startTime,
      endTime,
      step,
      enabled,
    })
  );

  return {
    series: data?.series ?? [],
    summary: data?.summary,
    isLoading: isLoading || isFetching,
    error,
    refetch,
  };
};
